import React from "react";
import classnames from "classnames";
import {
    StripeProvider,
    CardNumberElement,
    CardExpiryElement,
    CardCVCElement,
    Elements,
    injectStripe,
} from "react-stripe-elements";
import { connect } from "react-redux";
import * as actions from "../../actions/actions";

const nameError = {
    elementType: "cardName",
    error: {
        message: "Please enter the name as it appears on your card.",
    },
};

class _PaymentForm extends React.Component {
    constructor(props) {
        super(props);
        this.props.stripeClearErrors();
    }

    handleChange = (evt) => {
        this.props.stripeCardInputChange(evt);

        let cardName = this.refs.cardName.value;
        let billingZip = this.refs.billingZip.value;
        let billingStreetAddress = this.refs.billingStreetAddress.value;
        let validName = this.validName(cardName);
        let complete = evt.complete && validName;

        if (complete) {
            this.props.handleComplete(this.props.stripe, {
                name: cardName,
                address_zip: billingZip,
                address_line1: billingStreetAddress,
            });
        } else if (evt.error) {
            let { elementType } = evt;
            this.props.stripeCardInputError({ ...evt.error, elementType });
            this.props.handleComplete(false);
        } else if (!validName) {
            if (evt.complete) {
                this.props.stripeCardInputError({
                    ...nameError.error,
                    elementType: nameError.elementType,
                });
            }
            this.props.handleComplete(false);
        } else {
            this.props.handleComplete(false);
        }
    };

    validName(value) {
        return value && !!value.trim();
    }

    handleNameChange = (evt) => {
        let { value } = evt.target;
        let isValid = this.validName(value);
        if (isValid) {
            this.props.stripeCardInputChange({
                elementType: "cardName",
            });
            this.handleChange({ complete: true });
        } else {
            this.handleChange(nameError);
        }
    };

    render() {
        let style = {
            base: {
                color: "#424770",
                letterSpacing: "0.025em",
                fontFamily: "Light-45, Arial",
                lineHeight: "1.5em",
                fontSize: "18px",
                fontWeight: "normal",
                "::placeholder": {
                    color: "#BABABA",
                },
            },

            invalid: {
                color: "#9e2146",
            },
            disabled: {
                color: "#333333",
            },
        };

        let props = {
            onChange: this.handleChange,
            style: style,
        };

        let { fieldErrors, processing } = this.props;
        let { cardNumber, cardExpiry, cardCvc, cardName } = fieldErrors;

        let inputDisabled = !!processing || this.props.disabled;

        cardName = <FieldError name="Card Name" error={cardName} />;
        cardNumber = <FieldError name="Card Number" error={cardNumber} />;
        cardCvc = <FieldError name="CVC" error={cardCvc} />;
        cardExpiry = <FieldError name="Card Expiration" error={cardExpiry} />;
        const billingStreetAddress = <FieldError name="Billing Address" />;
        const billingZip = <FieldError name="Postal Code" />;

        let nameHasError = !!fieldErrors.cardName;
        let nameClasses = classnames("input", { error: nameHasError });

        let elementProps = {
            ...props,
            disabled: !!inputDisabled,
        };

        let classes = classnames("stripe-split-form", {
            "form-disabled": inputDisabled,
        });

        return (
            <ol className={classes}>
                <li>
                    <input
                        className={nameClasses}
                        type="text"
                        name="card_name"
                        id="card_name"
                        placeholder="Name on card"
                        required=""
                        ref="cardName"
                        autoFocus={this.props.turnOffAutofocus ? false : true}
                        disabled={inputDisabled}
                        onChange={this.handleNameChange}
                    />
                    {cardName}
                </li>
                <li>
                    <CardNumberElement
                        placeholder="Card Number"
                        {...elementProps}
                    />
                    {cardNumber}
                </li>
                <li>
                    <ul className="display-flex">
                        <li>
                            <CardExpiryElement {...elementProps} />
                            {cardExpiry}
                        </li>
                        <li>
                            <CardCVCElement {...elementProps} />
                            {cardCvc}
                        </li>
                    </ul>
                </li>
                <li>
                    <input
                        type="text"
                        className="input"
                        name="billing_street_address"
                        id="billing_street_address"
                        placeholder="Billing Address"
                        ref="billingStreetAddress"
                        disabled={inputDisabled}
                        onChange={() => this.handleChange({ complete: true })}
                    />
                    {billingStreetAddress}
                </li>
                <li>
                    <ul className="display-flex">
                        <li>
                            <input
                                type="text"
                                className="input"
                                name="billing_zip"
                                id="billing_zip"
                                placeholder="Postal Code"
                                ref="billingZip"
                                disabled={inputDisabled}
                                onChange={() =>
                                    this.handleChange({ complete: true })
                                }
                            />
                            {billingZip}
                        </li>
                        <li></li>
                    </ul>
                </li>
            </ol>
        );
    }
}

export const FieldError = ({ name, error }) =>
    error ? (
        <label className="show error on">{error}</label>
    ) : (
        <label className="on show">{name}</label>
    );

const PaymentForm = injectStripe(_PaymentForm);

const elementsFonts = [
    {
        family: "Light-45",
        src:
            window.location.protocol == "https:"
                ? `url(${require("../../../fonts/Light-45.woff2")})`
                : null,
        style: "normal",
    },
];

const StripeSplitForm = (props) => {
    if (!global.Stripe) {
        return (
            <div>
                <p style={{ color: "#F44E4E" }}>
                    Could not connect to Stripe, please disable any active
                    browser extensions and reload the page. <br />
                    <br />
                    If the problem persists, please let us know at{" "}
                    <a href="mailto:info@thenounproject.com" target="_blank">
                        info@thenounproject.com
                    </a>
                </p>
            </div>
        );
    }
    return (
        <StripeProvider apiKey={props.stripePublishableKey}>
            <Elements fonts={elementsFonts}>
                <PaymentForm {...props} />
            </Elements>
        </StripeProvider>
    );
};

function mapStateToProps(state, ownProps) {
    let { stripe } = state;
    let { fieldErrors, processing } = stripe;

    fieldErrors = fieldErrors || {};
    return { fieldErrors, processing };
}

export default connect(mapStateToProps, {
    stripeCardInputError: actions.stripeCardInputError,
    stripeCardInputChange: actions.stripeCardInputChange,
    stripeClearErrors: actions.stripeClearErrors,
})(StripeSplitForm);
